import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import type { ReactNode } from 'react';

import type { AppRouter } from '@infinex/main-service-contract/v2/client';

import type { MainServiceTrpcClient } from './utils/createMainServiceTrpcClient';

const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

export { useTRPC, useTRPCClient };

export interface MainServiceTrpcProviderProps {
  children: ReactNode;
  /**
   * A tRPC client created by {@link createMainServiceTrpcClient}.
   */
  trpcClient: MainServiceTrpcClient;
  /**
   * The TanStack `QueryClient` instance to use. Typically created via
   * {@link createMainServiceQueryClient}. The same instance is wired into both
   * `<QueryClientProvider>` and `<TRPCProvider>` so that tRPC and direct
   * `useQuery` calls share a single cache.
   */
  queryClient: QueryClient;
}

/**
 * Provider that wires a pre-created main-service tRPC client and a TanStack
 * `QueryClient` into React context, making `useTRPC()` and `useTRPCClient()`
 * available to all descendants.
 *
 * This is a single self-contained provider — you do **not** need a separate
 * `<QueryClientProvider>` wrapper; it is included here so that the query
 * cache is automatically shared between tRPC and any direct `useQuery` calls.
 *
 * @example
 * ```tsx
 * const queryClient = createMainServiceQueryClient();
 * const trpcClient  = createMainServiceClient({ url: `${workerUrl}/v2` });
 *
 * function Providers({ children }: { children: ReactNode }) {
 *   return (
 *     <MainServiceTrpcProvider trpcClient={trpcClient} queryClient={queryClient}>
 *       {children}
 *     </MainServiceTrpcProvider>
 *   );
 * }
 * ```
 */
export function MainServiceTrpcProvider({
  children,
  trpcClient,
  queryClient,
}: MainServiceTrpcProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
